import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { v4 as uuid } from 'uuid';

import { Modal, Input, Table, Row, Select, Flex, notification } from 'antd';
import { ColumnsType } from 'antd/es/table';

import {
    LoadingOutlined,
    ReloadOutlined,
    EditOutlined,
    DeleteOutlined,
    PlusOutlined,
    QuestionCircleOutlined,
    CheckOutlined,
    BuildOutlined,
} from '@ant-design/icons';

import ConsignmentCard, { IBoxGroupCardItem } from '@components/consignment-card/consignment-card';

import Tags from '@controls/tags/tags';
import Toolbar from '@controls/toolbar/toolbar';
import FormHeader from '@controls/form-header/form-header';
import Filter from '@controls/filter/filter';
import Comment from '@controls/comment/comment';

import { securityRestriction } from '@src/extensions/notification';
import { exception } from '@extensions/notification';
import { userLoaded, setFilter } from '@store/actions';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { delayAction } from '@extensions/utils';

import { IInboundCarrier } from '@src/entities/inbound-carrier';
import { IConsignmentFilter } from '@src/entities/consignment-filter';

import { IConsignment } from '@entities/consignment';
import { IUserSession } from '@src/entities/user-session';
import { ICountry } from '@entities/country';
import { ICity } from '@entities/city';
import { IUserFilter } from '@entities/user-filter';
import { IUser } from '@entities/user';

import { Permission, hasPermission } from '@src/enums/permission';
import { InboundCarrierStatus, enumLabel as inboundCarrierStatusLabel } from '@src/enums/inbound-carrier-status';
import { UserType } from '@enums/user-type';

import { serverFetch } from '@src/server';

dayjs.extend(utc);

const filterContext: string = 'ImportConsignments';

const Consignments = () => {
    const { carrierId } = useParams();

    const initFilter: IConsignmentFilter = { carrierIds: [carrierId as string], checkBoxNumbersExists: true };

    const d = useAppDispatch();
    const navigate = useNavigate();

    const [api, contextHolder] = notification.useNotification();
    const [modal, modalCntextHolder] = Modal.useModal();

    const userSession = useAppSelector<IUserSession>((s) => s.userSession);
    const filter = useAppSelector<IConsignmentFilter>((s) => s.filters[filterContext]);

    const [carrier, setCarrier] = useState<IInboundCarrier>();
    const [consignments, setConsignments] = useState<Array<IConsignment>>([]);
    const [selectedIds, setSelectedIds] = useState<React.Key[]>([]);
    const [currentConsignment, setCurrentConsignment] = useState<IConsignment>();
    const [countries, setCountries] = useState<Array<ICountry>>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [cities, setCities] = useState<Array<ICity>>([]);
    const [showFilter, setShowFilter] = useState<boolean>(true);
    const [users, setUsers] = useState<Array<IUser>>([]);

    const [refreshRequired, setRefreshRequired] = useState<boolean>(false);

    useEffect(() => {
        let cleanup = false;

        const fetchData = async () => {
            setLoading(true);

            const userFilter: IUserFilter = { types: [UserType.MainClient], isActive: true, isArchived: false };

            const promises = [
                await serverFetch('countries', { method: 'GET' })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения стран', ex, () => d(userLoaded(undefined)));
                    }),

                await serverFetch(`inboundcarriers/${carrierId}`, { method: 'GET' })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения рейса', ex, () => d(userLoaded(undefined)));
                    }),

                await serverFetch('cities', { method: 'GET' })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения городов', ex, () => d(userLoaded(undefined)));
                    }),

                await serverFetch('users', { method: 'GET', queryParams: userFilter })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения пользователей', ex, () => d(userLoaded(undefined)));
                    }),
            ];

            Promise.all([promises]).then((result) => {
                if (cleanup) return;

                setCountries(result[0][0]);
                setCarrier(result[0][1]);
                setCities(result[0][2]);
                setUsers(result[0][3]);

                setLoading(false);
                setRefreshRequired(false);
            });
        };

        fetchData();

        return () => {
            cleanup = true;
        };
    }, []);

    useEffect(() => {
        if (!refreshRequired) return;

        setSelectedIds([]);
        setCurrentConsignment(undefined);

        setRefreshRequired(false);
        setLoading(true);

        serverFetch(`consignments`, { method: 'GET', queryParams: filter })
            .then((data) => {
                setConsignments(data);
                setLoading(false);
            })
            .catch((ex) => {
                exception(api, 'Ошибка получения груза', ex, () => d(userLoaded(undefined)));
                setLoading(false);
            });
    }, [refreshRequired]);

    useEffect(() => {
        if (!filter || !filter.carrierIds?.find((t) => t == carrierId)) {
            d(setFilter(initFilter, filterContext));

            return;
        }

        delayAction(() => setRefreshRequired(true));
    }, [filter]);

    const onDelete = () => {
        if (!currentConsignment) return;

        serverFetch(`consignments/${currentConsignment.id}`, { method: 'DELETE' })
            .then(() => {
                onSelectChange([]);
                setRefreshRequired(true);
            })
            .catch((ex) => {
                exception(api, 'Ошибка удаления груза', ex, () => d(userLoaded(undefined)));
            });
    };

    const renderToolbar = () => {
        return (
            <Toolbar
                commands={[
                    {
                        label: 'Обновить',
                        key: 'refresh',
                        disabled: loading || !carrierId,
                        icon: <ReloadOutlined />,
                        onClick: () => {
                            setRefreshRequired(true);
                        },
                    },
                    {
                        label: 'Добавить партию',
                        disabled:
                            !carrierId ||
                            ((carrier?.status == InboundCarrierStatus.Finished || carrier?.status == InboundCarrierStatus.Cancelled) &&
                                !hasPermission(userSession.permissions, Permission.FullAccess)),
                        key: 'add',
                        icon: <PlusOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.ManageInboundCarrierConsignment)) {
                                securityRestriction(api, [Permission.ManageInboundCarrierConsignment]);
                                return;
                            }

                            navigate(`/logistics/import/consignments/new/${carrierId}`);
                        },
                    },
                    {
                        label: 'Добавить из предзагрузки',
                        disabled:
                            !carrierId ||
                            ((carrier?.status == InboundCarrierStatus.Finished || carrier?.status == InboundCarrierStatus.Cancelled) &&
                                !hasPermission(userSession.permissions, Permission.FullAccess)),
                        key: 'addPreloading',
                        icon: <BuildOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.ManageInboundCarrierConsignment)) {
                                securityRestriction(api, [Permission.ManageInboundCarrierConsignment]);
                                return;
                            }

                            navigate(`/logistics/import/consignments/preloading/${carrierId}`);
                        },
                    },
                    {
                        label: 'Изменить партию',
                        key: 'edit',
                        disabled:
                            !currentConsignment ||
                            consignments?.some((e) => e.hasBoxNumbers) ||
                            carrier?.status == InboundCarrierStatus.Finished ||
                            carrier?.status == InboundCarrierStatus.Cancelled,
                        icon: <EditOutlined />,
                        onClick: () => {
                            navigate(`/logistics/import/consignments/${carrierId}/${currentConsignment?.id}`);
                        },
                    },
                    {
                        label: 'Удалить партию',
                        key: 'delete',
                        disabled:
                            !currentConsignment ||
                            consignments?.some((e) => e.hasBoxNumbers) ||
                            carrier?.status == InboundCarrierStatus.Finished ||
                            carrier?.status == InboundCarrierStatus.Cancelled,
                        icon: <DeleteOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.ManageInboundCarrierConsignment)) {
                                securityRestriction(api, [Permission.ManageInboundCarrierConsignment]);
                                return;
                            }

                            modal.confirm({
                                title: `Удалить партию "${currentConsignment?.markingCode}"?`,
                                icon: <QuestionCircleOutlined />,
                                okType: 'primary',
                                okText: 'Удалить',
                                cancelText: 'Отмена',
                                onOk: () => {
                                    onDelete();
                                },
                            });
                        },
                    },
                    {
                        label: 'Завершить погрузку',
                        disabled: carrier?.status !== InboundCarrierStatus.New,
                        key: 'complete',
                        type: 'primary',
                        icon: <CheckOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.CompleteInboundConsignmentLoading)) {
                                securityRestriction(api, [Permission.CompleteInboundConsignmentLoading]);
                                return;
                            }

                            modal.confirm({
                                title: `Завершить погрузку?`,
                                icon: <QuestionCircleOutlined />,
                                okType: 'primary',
                                okText: 'ОК',
                                cancelText: 'Отмена',
                                onOk: () => {
                                    onCompleteLoading();
                                },
                            });
                        },
                    },
                ]}
                filter={{
                    active: showFilter,
                    onHideFilter: () => {
                        setShowFilter(!showFilter);
                    },
                }}
            />
        );
    };

    const onCompleteLoading = () => {
        setLoading(true);

        serverFetch(`inboundcarriers/completeloading/${carrierId}`, { method: 'POST' })
            .then(() => {
                setLoading(false);
                navigate(-1);
            })
            .catch((ex) => {
                setLoading(false);
                exception(api, 'Ошибка завершения погрузки', ex, () => d(userLoaded(undefined)));
            });
    };

    const onSelectChange = (selectedRowKeys: React.Key[]) => {
        setSelectedIds(selectedRowKeys);

        if (selectedRowKeys.length == 1) {
            const entity = consignments.find((o) => o.id === selectedRowKeys[0]);
            setCurrentConsignment(entity);
        } else {
            setCurrentConsignment(undefined);
        }
    };

    const renderFilter = () => {
        return (
            <Filter
                display={showFilter}
                items={[
                    <Select
                        key='users'
                        placeholder='Клиент'
                        maxTagCount='responsive'
                        // value={filter?.userId}
                        allowClear={true}
                        style={{ width: 200 }}
                        onChange={(value) => d(setFilter({ ...filter, userId: value }, filterContext))}
                        showSearch={{
                            filterOption: (input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase()),
                            filterSort: (a, b) => (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase()),
                        }}
                        /*  options={users.map((u) => {
                            return { value: u.id, label: `${u.code} / ${u.login}` };
                        })} */
                    />,
                    <Input
                        style={{ width: 130 }}
                        key='markingCode'
                        placeholder='Маркировка'
                        value={filter?.markingCode}
                        onChange={(data: React.ChangeEvent<HTMLInputElement>) => {
                            d(setFilter({ ...filter, markingCode: data.target.value }, filterContext));
                        }}
                    />,
                    <Input
                        style={{ width: 130 }}
                        key='awbNumber'
                        placeholder='AWB'
                        value={filter?.awbNumber}
                        onChange={(data: React.ChangeEvent<HTMLInputElement>) => {
                            d(setFilter({ ...filter, awbNumber: data.target.value }, filterContext));
                        }}
                    />,
                    <Select
                        key='city'
                        placeholder='Город доставки'
                        value={filter?.cityIds}
                        size='middle'
                        mode='multiple'
                        maxTagCount='responsive'
                        allowClear
                        style={{ width: 200 }}
                        onChange={(value) => d(setFilter({ ...filter, cityIds: value }, filterContext))}
                        showSearch={{
                            filterOption: (input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase()),
                            filterSort: (a, b) => (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase()),
                        }}
                        options={cities.map((s) => {
                            return { value: s.id, label: s.name };
                        })}
                    />,
                ]}
                onClear={() => d(setFilter(initFilter, filterContext))}
            />
        );
    };

    const renderTable = () => {
        const lines = consignments.map((l) => {
            const boxGroups: Array<any> = [];
            let totalQty: number = 0;
            let grossWeight: number = 0;
            let volumeWeight: number = 0;
            let totalVolume: number = 0;

            l.boxGroups?.map((b) => {
                boxGroups[b.countryId as any] = { qty: b.qty, loadingOn: b.loadingOn, awbNumber: b.awbNumber };
                totalQty += b.qty || 0;
                grossWeight += b.grossWeight || 0;
                volumeWeight += b.volumeWeight || 0;
                totalVolume += b.calcVolume || 0;
            });

            l = {
                ...l,
                ...boxGroups,
                totalQty: totalQty,
                grossWeight: grossWeight,
                volumeWeight: volumeWeight,
                totalVolume: totalVolume,
            };
            return l;
        });

        const columns: ColumnsType<IConsignment> = [
            {
                title: 'Клиент',
                align: 'center',
                dataIndex: 'userLogin',
                width: 150,
            },
            {
                width: 40,
                align: 'center',
                render: (_, record) => {
                    return record.comment && <Comment comment={record.comment} />;
                },
            },
            {
                title: 'Маркировка',
                dataIndex: 'markingCode',
                width: 200,
            },
            {
                title: 'Город доставки',
                dataIndex: 'cityName',
                width: 200,
                render: (_, record) => {
                    return (
                        <Flex>
                            <span style={{ marginRight: 10 }}>{record.cityName}</span>
                            <Tags label={record.tag} />
                        </Flex>
                    );
                },
            },
            {
                title: 'Кол-во / шт',
                align: 'center',
                dataIndex: 'totalQty',
                width: 120,
                onCell: () => ({
                    style: {
                        background: '#fafafa',
                        fontWeight: 600,
                    },
                }),
            },
            {
                title: 'Брутто / кг',
                align: 'center',
                dataIndex: 'grossWeight',
                width: 120,
                onCell: () => ({
                    style: {
                        background: '#fafafa',
                        fontWeight: 600,
                    },
                }),
            },
            {
                title: 'Объемный / кг',
                align: 'center',
                dataIndex: 'volumeWeight',
                width: 120,
                onCell: () => ({
                    style: {
                        background: '#fafafa',
                        fontWeight: 600,
                    },
                }),
            },
            {
                title: (
                    <>
                        Объем / м<sup>3</sup>
                    </>
                ),
                align: 'center',
                width: 120,
                onCell: () => ({
                    style: {
                        background: '#fff8d5',
                        fontWeight: 600,
                    },
                }),
                render: (_, record) => {
                    return record.totalVolume?.toFixed(2);
                },
            },
            {
                width: 375,
                render: (_, record) => {
                    return (
                        <Flex vertical>
                            {countries.map((c) => {
                                const boxGroups = record.boxGroups?.filter((bg) => bg.countryId === c.id);
                                if (!boxGroups || boxGroups.length <= 0) return <div key={uuid()}></div>;

                                return (
                                    <ConsignmentCard
                                        key={uuid()}
                                        country={c}
                                        api={api}
                                        items={boxGroups as Array<IBoxGroupCardItem>}
                                        serverFetch={serverFetch}
                                    />
                                );
                            })}
                        </Flex>
                    );
                },
            },
            {},
        ];

        return (
            <Table
                rowKey='id'
                size='small'
                rowSelection={{
                    selectedRowKeys: selectedIds,
                    onChange: onSelectChange,
                }}
                columns={columns}
                dataSource={lines}
                loading={{
                    spinning: loading,
                    indicator: <LoadingOutlined style={{ fontSize: 44 }} spin />,
                }}
                onRow={(record) => {
                    return {
                        onClick: (event) => {
                            onSelectChange([record.id || '']);
                        },
                    };
                }}
                pagination={false}
                scroll={{ y: `calc(100vh - ${showFilter ? '340px' : '282px'})`, x: 'max-content' }}
                summary={(pageData) => {
                    let qty = 0;
                    let totalGrossWeight = 0;
                    let totalVolumeWeight = 0;
                    let volume = 0;
                    pageData.forEach(({ totalQty, grossWeight, volumeWeight, totalVolume }) => {
                        qty += totalQty || 0;
                        totalGrossWeight += grossWeight || 0;
                        totalVolumeWeight += volumeWeight || 0;
                        volume += totalVolume || 0;
                    });
                    return (
                        <Table.Summary fixed>
                            <Table.Summary.Row>
                                <Table.Summary.Cell index={0} colSpan={5} align='left'>
                                    ИТОГО
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={5} align='center'>
                                    {qty}
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={6} align='center'>
                                    {totalGrossWeight}
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={7} align='center'>
                                    {totalVolumeWeight}
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={8} align='center'>
                                    {volume.toFixed(2)}
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={9}></Table.Summary.Cell>
                                <Table.Summary.Cell index={10}></Table.Summary.Cell>
                            </Table.Summary.Row>
                        </Table.Summary>
                    );
                }}
            />
        );
    };

    return (
        <>
            <Row>
                <FormHeader title={`Управление грузом (${carrier?.number} - "${inboundCarrierStatusLabel(carrier?.status)}")`} />
            </Row>
            {renderToolbar()}
            {renderFilter()}
            {renderTable()}

            {contextHolder}
            {modalCntextHolder}
        </>
    );
};

export default Consignments;
